import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { COMPANY } from "@/lib/constants";

// Reads the current day/hour in the business's local timezone (Central Time),
// independent of the visitor's own timezone.
function nowInBusinessTz() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: COMPANY.timezone,
    weekday: "short",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  }).formatToParts(new Date());
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  const dowIndex = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[map.weekday];
  const hour = parseInt(map.hour, 10) % 24;
  const minute = parseInt(map.minute, 10);
  return { dow: dowIndex, decimalHour: hour + minute / 60 };
}

function getStatus() {
  const { dow, decimalHour } = nowInBusinessTz();
  const today = COMPANY.hoursDetail.find((d) => d.dow === dow);
  const isOpen = today && today.open !== null && decimalHour >= today.open && decimalHour < today.close;
  let closingSoon = false;
  if (isOpen && today.close - decimalHour <= 1) closingSoon = true;
  return { dow, isOpen, closingSoon, today };
}

export default function BusinessHours({ variant = "card" }) {
  const [status, setStatus] = useState(getStatus);

  useEffect(() => {
    const id = setInterval(() => setStatus(getStatus()), 60000);
    return () => clearInterval(id);
  }, []);

  const { dow, isOpen, closingSoon } = status;
  const ordered = COMPANY.hoursDetail; // Mon → Sun

  return (
    <div
      className="relative overflow-hidden bg-secondary text-white border border-white/10 grain-texture"
      data-testid="business-hours"
    >
      {/* accent glow */}
      <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-primary/20 blur-3xl" aria-hidden="true" />

      <div className="relative p-6 sm:p-7">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center h-9 w-9 rounded-sm bg-primary/15 text-primary">
              <Clock className="h-5 w-5" />
            </span>
            <h3 className="font-heading text-lg font-extrabold tracking-tight">Business Hours</h3>
          </div>

          {/* Live open/closed pill */}
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${
              isOpen ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"
            }`}
            data-testid="business-hours-status"
          >
            <span className="relative flex h-2 w-2">
              {isOpen && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              )}
              <span className={`relative inline-flex h-2 w-2 rounded-full ${isOpen ? "bg-emerald-400" : "bg-red-400"}`} />
            </span>
            {isOpen ? (closingSoon ? "Closing soon" : "Open now") : "Closed"}
          </span>
        </div>

        <ul className="divide-y divide-white/10">
          {ordered.map((d) => {
            const isToday = d.dow === dow;
            const closed = d.open === null;
            return (
              <li
                key={d.dow}
                className={`flex items-center justify-between py-2.5 text-sm transition-colors ${
                  isToday ? "-mx-3 px-3 rounded-sm bg-primary/10" : ""
                }`}
                data-testid={`hours-row-${d.label.toLowerCase()}`}
              >
                <span className={`flex items-center gap-2 ${isToday ? "font-bold text-white" : "text-white/70"}`}>
                  {d.label}
                  {isToday && (
                    <span className="rounded-sm bg-primary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                      Today
                    </span>
                  )}
                </span>
                <span className={`font-semibold ${closed ? "text-white/40" : isToday ? "text-primary" : "text-white/80"}`}>
                  {d.display}
                </span>
              </li>
            );
          })}
        </ul>

        <p className="mt-5 text-xs text-white/45 leading-relaxed">
          Emergency &amp; after-hours calls welcome — leave a message and we'll get right back to you.
        </p>
      </div>
    </div>
  );
}
